import * as Sentry from '@sentry/react';
import { APP_ENV } from '@config/config.ts';

const TRACES_SAMPLE_RATE: Record<string, number> = {
  production: 0.1,
  uat: 0.2,
  development: 0.5,
};

export const initSentry = (dsn: string, isSentryDisabled = false) => {
  if (isSentryDisabled || !dsn) {
    console.log('Sentry is disabled');
    return;
  }

  const env = APP_ENV || 'production';
  const tracesSampleRate = TRACES_SAMPLE_RATE[env] ?? 0.1;

  console.log(`Sentry is enabled for environment: ${env}`);

  Sentry.init({
    dsn,
    environment: env,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: false,
        mask: ['.chatbot-message-content', '[data-sentry-mask]'],
        unmask: ['.sentry-unmask'],
        networkDetailAllowUrls: [
          /^https:\/\/.*\.fibonaccipartner\.com\/api/,
          /^http:\/\/localhost/,
        ],
        networkCaptureBodies: false,
        networkRequestHeaders: ['X-Request-Id', 'X-Conversation-Id'],
        networkResponseHeaders: ['X-Request-Id'],
      }),
      Sentry.feedbackIntegration({
        colorScheme: 'system',
        triggerLabel: 'Send Report',
        enableScreenshot: true,
        submitButtonLabel: 'Send Feedback',
        formTitle: 'Send Feedback',
        autoInject: false,
      }),
    ],
    tracePropagationTargets: [
      /^https:\/\/.*\.fibonaccipartner\.com/,
      /^http:\/\/localhost/,
    ],
    tracesSampleRate,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    sendDefaultPii: false,
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.category === 'console') {
        const msg = String(breadcrumb.message || '');
        if (msg.length > 500) {
          breadcrumb.message = msg.slice(0, 500) + '… [truncated]';
        }
      }
      if (breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr') {
        if (breadcrumb.data?.url) {
          try {
            const url = new URL(breadcrumb.data.url, window.location.origin);
            url.searchParams.delete('token');
            url.searchParams.delete('auth');
            url.searchParams.delete('key');
            breadcrumb.data.url = url.toString();
          } catch {
            // leave URL as-is if parsing fails
          }
        }
        delete breadcrumb.data?.request_body;
        delete breadcrumb.data?.response_body;
      }
      return breadcrumb;
    },
    beforeSend(event) {
      if (event.request?.data) {
        const data = typeof event.request.data === 'string'
          ? event.request.data
          : JSON.stringify(event.request.data);
        if (data.length > 1000) {
          event.request.data = '[redacted – large payload]';
        }
      }
      return event;
    },
  });
};

